// Who can fill a slot (DIRECTION U21, B-320): the one rule behind
// `PlayersQuery.slot`, which the league store and the simulator both answer
// with, so a client asks for a slot rather than learning its positions.
//
// Pure and sport-free: the slot's shape and the league's IR rule are handed in.

import { qualifiesForReserve, type ReserveRule } from './reserve';
import { qualifiesForTaxi, type TaxiRule } from './taxi';

/** A slot as a roster shape has it: its kind, and the positions a starting slot takes. */
export interface FillableSlot {
  kind: string;
  /** Empty means any position. */
  eligible: readonly string[];
}

/**
 * Whether this player can fill this slot: a starting slot by the positions it
 * takes (a FLEX's three; none named is any), the bench by anyone, the
 * reserve only by a status the league lets go there, and the taxi squad only
 * by a player within its years in the league (PLAN-2026-10-27-the-next-season
 * §2.7). Whoever is in the slot now does not enter into it, nor whether the
 * taxi squad is still taking players, which is the save's to say.
 */
export function canFillSlot(
  slot: FillableSlot,
  reserve: Pick<ReserveRule, 'eligibleStatuses'> | null,
  player: {
    positions: readonly string[];
    injuryStatus: string | null;
    /** Seasons in the pro league; absent or null when nobody knows. */
    experienceYears?: number | null;
  },
  taxi: Pick<TaxiRule, 'maxYearsExperience'> | null = null,
): boolean {
  switch (slot.kind) {
    case 'starter':
      return slot.eligible.length === 0 || player.positions.some((p) => slot.eligible.includes(p));
    case 'reserve':
      return reserve !== null && qualifiesForReserve(reserve, player.injuryStatus);
    case 'taxi':
      return taxi !== null && qualifiesForTaxi(taxi, player.experienceYears ?? null);
    default:
      return true;
  }
}
